import React from 'react';
import { ShieldCheck, ShieldAlert, ShieldX, Award } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * TrustBadge - shows a seller's Trust tier as a compact pill.
 *
 * Tiers:
 *   GOLD       (80-100) → gold pill + Award icon
 *   VERIFIED   (60-79)  → blue pill + ShieldCheck icon
 *   UNVERIFIED (0-59)   → grey pill + ShieldAlert icon
 *   BLOCKED             → red pill + ShieldX icon
 */
const TIER_CONFIG = {
  GOLD: {
    label: 'Trusted Gold',
    className: 'bg-gradient-to-r from-amber-400 to-yellow-500 text-white border-amber-500',
    Icon: Award,
  },
  VERIFIED: {
    label: 'Verified',
    className: 'bg-blue-100 text-blue-800 border-blue-300',
    Icon: ShieldCheck,
  },
  UNVERIFIED: {
    label: 'Unverified',
    className: 'bg-gray-100 text-gray-600 border-gray-300',
    Icon: ShieldAlert,
  },
  BLOCKED: {
    label: 'Blocked',
    className: 'bg-red-100 text-red-800 border-red-300',
    Icon: ShieldX,
  },
};

const SIZE_CONFIG = {
  xs: { pill: 'px-1.5 py-0 text-[10px] gap-0.5', icon: 'h-2.5 w-2.5' },
  sm: { pill: 'px-2 py-0.5 text-xs gap-1', icon: 'h-3 w-3' },
  md: { pill: 'px-2.5 py-1 text-sm gap-1.5', icon: 'h-4 w-4' },
  lg: { pill: 'px-3 py-1.5 text-base gap-2', icon: 'h-5 w-5' },
};

export const TrustBadge = ({ tier = 'UNVERIFIED', size = 'sm', score = null, showLabel = true, className = '' }) => {
  const cfg = TIER_CONFIG[tier] || TIER_CONFIG.UNVERIFIED;
  const sz = SIZE_CONFIG[size] || SIZE_CONFIG.sm;
  const { Icon } = cfg;
  return (
    <span
      data-testid={`trust-badge-${tier.toLowerCase()}`}
      className={cn(
        'inline-flex items-center rounded-full border font-semibold whitespace-nowrap',
        cfg.className,
        sz.pill,
        className,
      )}
      title={score !== null ? `Trust score ${score}/100` : cfg.label}
    >
      <Icon className={sz.icon} />
      {showLabel && <span>{cfg.label}</span>}
      {score !== null && showLabel && size !== 'xs' && (
        <span className="opacity-80 font-normal">· {Math.round(score)}</span>
      )}
    </span>
  );
};

export default TrustBadge;
